/** Normalize unified review queue rows, including readable pre-v3 history. */
import { procedureTitle, profileEntries } from "@/lib/knowledge-kinds";
import { reviewReasonLabel } from "@/lib/memory-review-reasons";

import type { MemoryReviewItem } from "./memory";

export interface MemoryReviewWire {
  id: string;
  semantic_key: string;
  type: string;
  payload: Record<string, unknown>;
  status: string;
  evidence_mode: string;
  source_quote: string;
  confidence: number | null;
  policy_reason: string;
  application?: { undone: boolean; undo_available: boolean } | null;
  created_at: string;
  node_id?: string | null;
  current_value?: Record<string, unknown> | null;
  source_node_id?: string | null;
  target_node_id?: string | null;
}

function text(value: unknown): string {
  return typeof value === "string" ? value.trim() : "";
}

function record(value: unknown): Record<string, unknown> {
  return value && typeof value === "object" && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : {};
}

const REVIEW_TITLES: Readonly<
  Record<string, (payload: Record<string, unknown>) => string>
> = {
  fact: (payload) => text(payload.claim),
  entity: (payload) => text(payload.name),
  profile: (payload) =>
    profileEntries(payload)
      .map(([, label, value]) => `${label}: ${value}`)
      .join(" · "),
  procedure: (payload) => procedureTitle(payload),
  knowledge: (payload) => text(record(payload.payload).statement),
};

export function normalizeReviewItem(item: MemoryReviewWire): MemoryReviewItem {
  const reason = item.policy_reason || "review_required";
  const current = record(item.current_value);
  return {
    id: item.id,
    node_id: item.node_id ?? null,
    item_type: item.type,
    status:
      item.status === "quarantined"
        ? "pending"
        : (item.status as MemoryReviewItem["status"]),
    title: REVIEW_TITLES[item.type]?.(item.payload) || item.semantic_key,
    reason_code: reason,
    reason_label: reviewReasonLabel(reason),
    confidence: item.confidence,
    current_value: text(current.claim) || text(current.value) || null,
    proposed_value: item.payload,
    source_node_id: item.source_node_id ?? null,
    target_node_id: item.target_node_id ?? null,
    attributed: item.evidence_mode.startsWith("explicit_"),
    source_quote: item.source_quote,
    undo_available:
      item.status === "applied" && item.application?.undo_available === true,
    created_at: item.created_at,
  };
}
